import { Button, InputSelect } from '@vrplatform/components';
import { useBilling, useTeam } from '@vrplatform/data';
import {
  Box,
  Group,
  InputWrapper,
  Modal,
  rem,
  Text,
  Title,
} from '@mantine/core';
import { type ComponentProps, type ReactNode, useState } from 'react';
import { ExtractSingleModal } from './ExtractSingleModal';
import type { ExportSingleImportUniqueRef, ExtractConnection } from './_types';
import { useDisclosure } from '@mantine/hooks';
import { Controller, useForm } from 'react-hook-form';
import { Avatar } from '@mantine/core';
import { Center } from '@mantine/core';
import { AppIconById } from '../../components';
import { ReservationCreateModal } from './ReservationCreateModal';

type ButtonProps = ComponentProps<typeof Button>;

type Props = {
  variant?: ButtonProps['variant'];
  children?: ReactNode;
  size?: ButtonProps['size'];
  uniqueRef: ExportSingleImportUniqueRef;
  icon?: ButtonProps['leftIcon'];
};

export const ExtractSingleButton = (props: Props) => {
  'use-memo';

  const {
    variant = 'transparent',
    children,
    uniqueRef,
    size,
    icon,
    // connectionId: initialConnectionId,
  } = props;

  const { isReadOnly } = useBilling();
  const [{ extractableConnections }] = useTeam();
  const [importConnection, setImportConnection] = useState<ExtractConnection>();

  const connections = extractableConnections?.[uniqueRef]?.connections ?? [];

  const [
    openedSelectModal,
    { open: openSelectModal, close: closeSelectModal },
  ] = useDisclosure(false);

  const disabled = !connections?.length;

  if (isReadOnly) return null;

  return (
    <>
      {uniqueRef === 'reservation' && disabled ? (
        <AddReservationButton variant={variant} size={size} icon={icon}>
          {children}
        </AddReservationButton>
      ) : (
        <Button
          leftIcon={icon || 'CloudDownloadIcon'}
          size={size}
          variant={variant}
          onClick={() => {
            if (connections?.length === 1) {
              setImportConnection(connections[0]);
            } else {
              openSelectModal();
            }
          }}
          disabled={disabled}
        >
          {children || `Fetch ${uniqueRef}`}
        </Button>
      )}

      <ExtractSingleModal
        connection={importConnection}
        closeModal={() => setImportConnection(undefined)}
        uniqueRef={uniqueRef}
      />

      <SelectConnectionModal
        connections={connections}
        closeModal={closeSelectModal}
        opened={openedSelectModal}
        openExtractModal={setImportConnection}
      />
    </>
  );
};

const AddReservationButton = ({
  variant = 'transparent',
  children,
  size,
  icon,
}: Omit<Props, 'uniqueRef'>) => {
  const [opened, handlers] = useDisclosure(false);

  return (
    <>
      <Button
        leftIcon={icon || 'CloudDownloadIcon'}
        size={size}
        variant={variant}
        onClick={handlers.open}
      >
        {children || 'Add Reservation'}
      </Button>

      <ReservationCreateModal
        opened={opened}
        closeModal={handlers.close}
        openExtractModal={undefined}
      />
    </>
  );
};

const SelectConnectionModal = ({
  connections,
  closeModal: cl,
  opened,
  openExtractModal,
}: {
  connections: ExtractConnection[];
  closeModal: () => void;
  opened: boolean;
  openExtractModal: (connection: ExtractConnection) => void;
}) => {
  type FormInputs = {
    connectionId: string | null;
  };

  const uniqueAppIds = [
    ...new Set(connections.map((connection) => connection.appId)),
  ];

  const methods = useForm<FormInputs>({
    values: {
      connectionId: null,
    },
  });

  const closeModal = () => {
    cl();
    methods.reset({
      connectionId: null,
    });
  };

  const submit = (data: FormInputs) => {
    const connection = connections.find(
      (connection) => connection.id === data.connectionId
    );
    if (!connection) return;
    openExtractModal(connection);
    closeModal();
  };

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      size={450}
      radius="md"
      styles={(theme) => ({
        content: {
          padding: theme.spacing.md,
          paddingTop: 0,
        },
      })}
    >
      <Center mb="lg">
        <Avatar.Group>
          {uniqueAppIds.map((appId) => (
            <AppIconById key={appId} appId={appId} size={30} />
          ))}
        </Avatar.Group>
      </Center>

      <Title ta="center" order={3} mb="md">
        Select connection to import data to
      </Title>

      <Text component="p" c="gray" ta="center" mb="md">
        You have multiple connections to extract data from.
        <br />
        Please select the connection that you want to associate with this
        import.
      </Text>

      <Box
        component="form"
        onSubmit={methods.handleSubmit(submit)}
        onReset={closeModal}
      >
        <Controller
          control={methods.control}
          name="connectionId"
          rules={{
            required: 'Please select a connection',
          }}
          render={({ field, fieldState }) => {
            const options = connections.map((connection) => ({
              value: connection.id,
              label: connection.name,
            }));

            const value =
              options.find((option) => option.value === field.value) ?? null;

            return (
              <InputWrapper
                mb={rem(40)}
                mt="lg"
                label="Connection"
                error={fieldState.error?.message}
              >
                <InputSelect
                  type="single"
                  value={value}
                  data={{
                    options,
                  }}
                  setValue={(v) => field.onChange(v?.value || null)}
                  inputProps={{
                    error: !!fieldState.error?.message,
                    placeholder: `Select connection`,
                    withClearButton: true,
                  }}
                  dropdownProps={{
                    withinPortal: true,
                    width: 'target',
                  }}
                />
              </InputWrapper>
            );
          }}
        />

        <Group wrap="nowrap">
          <Button type="reset" disabled={methods.formState.isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={methods.formState.isSubmitting}
            disabled={!methods.formState.isValid}
            sx={{
              flex: 1,
            }}
          >
            Submit
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
